import { boundMethod } from 'autobind-decorator';
import type { Container, InteractionEvent } from 'pixi.js';

import type { EditorContext } from 'flow-builder/types/context';
import { HighlightType } from 'flow-builder/types/node';

import { Movement, NodeMoveType } from '../../hooks/node/useMove/useMove';
import { HighlightMatch } from '../../hooks/useHighlight';
import type { Relation } from '../../types/relation';

export interface Bounds {
  x: [number, number];
  y: [number, number];
}

export const getIsInBounds = (
  pageX: Relation['pageX'],
  pageY: Relation['pageY'],
  { x: boundsX, y: boundsY }: Bounds,
): boolean =>
  boundsX[0] < pageX &&
  boundsX[1] > pageX &&
  boundsY[0] < pageY &&
  boundsY[1] > pageY;

interface DragListenerProps {
  node: Relation;
  container: Container;
  editorContext: EditorContext;
  onDragStart?: (evt: MouseEvent) => void;
  onDragEnd?: (evt: MouseEvent) => void;
  onDragMove?: (evt: MouseEvent) => void;
}

interface DragListenerState {
  isDragging: boolean;
  lastEvent: MouseEvent | null;
  position: { x: number; y: number };
  nodesToDrag: Relation['id'][];
}

const NIL_MOVEMENT: Movement = { x: 0, y: 0 };

export class DragListener {
  public props: DragListenerProps;
  public state: DragListenerState;

  constructor(props: DragListenerProps) {
    this.props = props;
    this.state = {
      isDragging: false,
      lastEvent: null,
      position: { x: 0, y: 0 },
      nodesToDrag: [],
    };
  }

  public addListeners(): void {
    const { container } = this.props;

    container.on('mousedown', this.onMouseDown);
  }

  public removeListeners(): void {
    const { container } = this.props;

    container.off('mousedown');
    window.removeEventListener('mouseup', this.onMouseUp);
    window.removeEventListener('mousemove', this.onMouseMove);
  }

  @boundMethod
  private onMouseDown(_: InteractionEvent) {
    const {
      node: { id },
      editorContext,
    } = this.props;
    const {
      state: { highlightState },
    } = editorContext;
    const { viewport } = editorContext.view;

    if (!viewport) {
      return;
    }

    this.state.isDragging = true;

    const highlightNode = highlightState.nodes[id];

    if (
      highlightNode &&
      highlightNode.match !== HighlightMatch.Connection &&
      highlightState.type === HighlightType.INFO
    ) {
      this.state.nodesToDrag = Object.keys(highlightState.nodes).filter(
        (id) =>
          highlightState.nodes[id].match === HighlightMatch.Node ||
          highlightState.nodes[id].match === HighlightMatch.Both,
      );
    } else {
      this.state.nodesToDrag = [id];
    }

    viewport.plugins.pause('drag');

    window.addEventListener('mouseup', this.onMouseUp);
    window.addEventListener('mousemove', this.onMouseMove);
  }

  @boundMethod
  private onMouseUp(event: MouseEvent) {
    const { onDragEnd, editorContext } = this.props;
    const { lastEvent } = this.state;
    const { viewport } = editorContext.view;

    if (!viewport) {
      return;
    }

    if (lastEvent) {
      this.emitDragEvent(NIL_MOVEMENT, NodeMoveType.End);
      onDragEnd?.(event);
    }

    this.state.isDragging = false;
    this.state.lastEvent = null;
    this.state.position.x = 0;
    this.state.position.y = 0;
    this.state.nodesToDrag = [];

    window.removeEventListener('mouseup', this.onMouseUp);
    window.removeEventListener('mousemove', this.onMouseMove);

    viewport.plugins.resume('drag');
  }

  @boundMethod
  private onMouseMove(event: MouseEvent) {
    const { onDragStart, onDragMove, editorContext } = this.props;
    const { viewport } = editorContext.view;
    const { pageX, pageY } = event;

    if (!viewport || !this.state.isDragging) {
      return;
    }

    if (!this.state.lastEvent) {
      onDragStart?.(event);
      this.state.position.x = event.pageX;
      this.state.position.y = event.pageY;
      this.emitDragEvent(NIL_MOVEMENT, NodeMoveType.Start);
    }

    event.stopPropagation();
    this.state.lastEvent = event;

    const { position } = this.state;
    const scale = viewport.scale.x;

    const movement = {
      x: (pageX - position.x) / scale,
      y: (pageY - position.y) / scale,
    };

    this.state.position.x = pageX;
    this.state.position.y = pageY;

    this.emitDragEvent(movement, NodeMoveType.Drag);
    onDragMove?.(event);
  }

  private emitDragEvent(movement: Movement, type: NodeMoveType) {
    const { nodesToDrag } = this.state;
    const { editorContext } = this.props;
    const {
      handlers: {
        node: { onMove },
      },
    } = editorContext;
    const { viewport, cull } = editorContext.view;

    onMove({
      nodes: nodesToDrag,
      movement,
      type,
    });

    if (nodesToDrag.length <= 1 || !viewport || !cull) {
      return;
    }

    const bounds = viewport.getVisibleBounds();

    cull.cull(bounds);
    viewport.dirty = false;
  }
}
